import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { pluginRegistry } from '../plugins/pluginRegistry';
import { validateManifest, type PluginManifest } from '../plugins/pluginManifest';
import { BUNDLED_PLUGIN_MANIFESTS, pluginHost } from '../plugins/pluginHost';
import {
  listPlugins,
  listInstalled,
  installPlugin,
  uninstallPlugin,
  type Plugin,
} from '../lib/marketplaceApi';
import { PluginConsentModal } from './PluginConsentModal';
import { PluginReportModal } from './PluginReportModal';
import { PluginPublisherPanel } from './PluginPublisherPanel';
import type { InstalledPlugin } from '../lib/marketplaceApi';
import { useAuthStore } from '../stores/authStore';

export interface MarketplaceItem {
  id: string;
  name: string;
  author: string;
  description: string;
  version: string;
  category: string;
  downloads: number;
  installed: boolean;
}

// ─── Hardcoded available plugins (catalogue) ──────────────────────────────────

// Inline bundled examples (actually runnable) come first; the remote catalogue
// entries below are retained for completeness but point at a CDN so they only
// load when the network fetch succeeds.
const AVAILABLE_PLUGINS: PluginManifest[] = [
  ...BUNDLED_PLUGIN_MANIFESTS,
  {
    id: 'structural-grid-tool',
    name: 'Structural Grid Tool',
    version: '1.3.0',
    description: 'Generate and manage structural grids with parametric column placement.',
    permissions: ['ui', 'document'],
    entrypoint: 'https://cdn.opencad.archi/plugins/structural-grid/index.js',
  },
  {
    id: 'energy-analysis-pro',
    name: 'Energy Analysis Pro',
    version: '2.1.0',
    description: 'Run HVAC and thermal energy simulations directly in the browser.',
    permissions: ['network', 'ui', 'document'],
    entrypoint: 'https://cdn.opencad.archi/plugins/energy-analysis/index.js',
  },
  {
    id: 'bim-clash-detector',
    name: 'BIM Clash Detector',
    version: '1.0.4',
    description: 'Detect and report clashes between building elements across disciplines.',
    permissions: ['document', 'ui'],
    entrypoint: 'https://cdn.opencad.archi/plugins/clash-detector/index.js',
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface MarketplacePanelProps {
  items?: MarketplaceItem[];
  onInstall?: (item: MarketplaceItem) => void;
  onPublish?: () => void;
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function PluginSkeleton(): React.ReactElement {
  return (
    <div className="marketplace-item skeleton" aria-busy="true">
      <div className="item-info">
        <span className="skeleton-line skeleton-name" />
        <span className="skeleton-line skeleton-desc" />
        <span className="skeleton-line skeleton-meta" />
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function MarketplacePanel({
  items = [],
  onInstall,
  onPublish: _onPublish,
}: MarketplacePanelProps = {}) {
  void _onPublish;
  const { t } = useTranslation('panels');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // API state
  const [apiPlugins, setApiPlugins] = useState<Plugin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [installingIds, setInstallingIds] = useState<Set<string>>(new Set());
  const [uninstallingIds, setUninstallingIds] = useState<Set<string>>(new Set());

  // Track which plugin IDs are currently installed in the local registry.
  // Subscribe so the UI reflects installs/uninstalls from any code path
  // (including host-driven reloads after a refresh).
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(
    () => new Set(pluginRegistry.list().map((m) => m.id)),
  );

  // Plugin pending consent approval. When non-null, PluginConsentModal
  // renders and blocks the install until the user clicks Install or
  // Cancel. We stash either a backend Plugin (for the remote-catalogue
  // path) or a local PluginManifest (for the bundled fallback path) so
  // the same dialog handles both.
  type ConsentPending =
    | { kind: 'remote'; plugin: Plugin }
    | { kind: 'bundled'; manifest: PluginManifest };
  const [pendingConsent, setPendingConsent] = useState<ConsentPending | null>(null);

  // Target of the report-a-plugin modal (null = closed).
  const [reportingPlugin, setReportingPlugin] = useState<{ id: string; name: string } | null>(null);
  const [showPublisher, setShowPublisher] = useState(false);
  const userEmail = useAuthStore((s) => s.user?.email ?? undefined);

  // Per-user installed plugins from the backend. Used to detect available
  // updates (installedVersion !== version) and to surface the revoked
  // flag so we can warn the user their plugin was killed server-side.
  const [installedFromServer, setInstalledFromServer] = useState<InstalledPlugin[]>([]);
  useEffect(() => {
    return pluginRegistry.subscribe(() => {
      setRegisteredIds(new Set(pluginRegistry.list().map((m) => m.id)));
    });
  }, []);

  // ── Debounce search input 300 ms ─────────────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // ── Fetch plugins from API ───────────────────────────────────────────────
  const fetchPlugins = useCallback(async (searchTerm: string) => {
    setLoading(true);
    setError(null);
    try {
      const [all, installed] = await Promise.all([
        listPlugins(searchTerm ? { search: searchTerm } : undefined),
        listInstalled(),
      ]);
      const installedIds = new Set(installed.map((p) => p.id));
      const merged = all.map((p) => ({ ...p, installed: p.installed || installedIds.has(p.id) }));
      setApiPlugins(merged);
      setInstalledFromServer(installed);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load plugins';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and when debounced search changes
  useEffect(() => {
    void fetchPlugins(debouncedSearch);
  }, [debouncedSearch, fetchPlugins]);

  // ── Install handlers ─────────────────────────────────────────────────────
  // Button click opens the consent modal. The modal's Install button calls
  // performApiInstall / performBundledInstall below to actually register
  // the manifest and round-trip to the server.

  const handleApiInstall = useCallback((plugin: Plugin) => {
    setPendingConsent({ kind: 'remote', plugin });
  }, []);

  // Update handler: when the catalogue version is ahead of the user's
  // installedVersion, one click replaces the running sandbox with the
  // new entrypoint and tells the server to bump the installed version.
  const handleApiUpdate = useCallback(async (plugin: Plugin) => {
    const pluginId = plugin.id;
    const nextManifest: PluginManifest = {
      id: plugin.id,
      name: plugin.name,
      version: plugin.version,
      description: plugin.description,
      permissions: plugin.permissions,
      entrypoint: plugin.entrypoint,
      sriHash: plugin.sriHash,
      icon: plugin.icon,
      author: plugin.author,
    };
    if (!validateManifest(nextManifest)) return;

    setInstallingIds((prev) => new Set([...prev, pluginId]));
    try {
      // Stop the currently-running sandbox before re-registering so the
      // host's subscribe callback starts the new version against the new
      // manifest instead of keeping the old worker alive.
      pluginHost.stop(pluginId);
      pluginRegistry.register(nextManifest);
      await installPlugin(pluginId); // bumps installed version server-side
      // Refresh installedFromServer so the Update button goes away.
      setInstalledFromServer((prev) =>
        prev.map((i) => (i.id === pluginId ? { ...i, installedVersion: plugin.version } : i)),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[marketplace] update failed for ${pluginId}:`, err);
    } finally {
      setInstallingIds((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    }
  }, []);

  // Lookup helper: does `pluginId` have an available update?
  const installedById = useMemo(() => {
    const m = new Map<string, InstalledPlugin>();
    for (const p of installedFromServer) m.set(p.id, p);
    return m;
  }, [installedFromServer]);
  const hasUpdate = useCallback(
    (plugin: Plugin): boolean => {
      const i = installedById.get(plugin.id);
      return !!i && i.installedVersion !== plugin.version;
    },
    [installedById],
  );

  /** Runs once the user clicks Install in the consent modal for a
   *  remote-catalogue plugin. Registers the manifest locally (so
   *  pluginHost spawns a worker) AND records the install on the server
   *  (so the user's library follows them across devices and the download
   *  counter increments). If the server call fails we revert both the UI
   *  and the local registration so the Install button reappears. */
  const performApiInstall = useCallback(async (plugin: Plugin) => {
    const pluginId = plugin.id;
    const manifest: PluginManifest = {
      id: plugin.id,
      name: plugin.name,
      version: plugin.version,
      description: plugin.description,
      permissions: plugin.permissions,
      entrypoint: plugin.entrypoint,
      sriHash: plugin.sriHash,
      icon: plugin.icon,
      author: plugin.author,
    };
    if (!validateManifest(manifest)) return;

    setApiPlugins((prev) => prev.map((p) => (p.id === pluginId ? { ...p, installed: true } : p)));
    setInstallingIds((prev) => new Set([...prev, pluginId]));
    pluginRegistry.register(manifest);
    try {
      await installPlugin(pluginId);
    } catch (err) {
      // Server rejected — revert UI + local state.
      setApiPlugins((prev) => prev.map((p) => (p.id === pluginId ? { ...p, installed: false } : p)));
      pluginRegistry.unregister(pluginId);
      // eslint-disable-next-line no-console
      console.warn(`[marketplace] install failed for ${pluginId}:`, err);
    } finally {
      setInstallingIds((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    }
  }, []);

  // ── Uninstall handler ────────────────────────────────────────────────────
  // Unregisters locally first (stops the running worker) then DELETEs the
  // install record on the server. Uninstall is idempotent server-side, so
  // a network failure here just means the server-side record lingers; the
  // client still sees the plugin as gone, and the next installed-list
  // reconcile will clear the discrepancy.
  const handleApiUninstall = useCallback(async (pluginId: string) => {
    setApiPlugins((prev) =>
      prev.map((p) => (p.id === pluginId ? { ...p, installed: false } : p)),
    );
    setUninstallingIds((prev) => new Set([...prev, pluginId]));
    pluginRegistry.unregister(pluginId);
    try {
      await uninstallPlugin(pluginId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[marketplace] uninstall server call failed for ${pluginId}:`, err);
    } finally {
      setUninstallingIds((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    }
  }, []);

  // ── Bundled-catalogue install / uninstall ────────────────────────────────
  // These operate on the local registry only (no backend call). The
  // registry persists to localStorage and the pluginHost subscribes to
  // mutations, so install → worker starts, uninstall → worker disposes and
  // the plugin's registered commands are purged.
  const handleInstallPlugin = (manifest: PluginManifest) => {
    if (!validateManifest(manifest)) return;
    setPendingConsent({ kind: 'bundled', manifest });
  };
  const performBundledInstall = (manifest: PluginManifest) => {
    pluginRegistry.register(manifest);
  };
  const handleUninstallPlugin = (pluginId: string) => {
    pluginRegistry.unregister(pluginId);
  };

  // ── Filter legacy prop items by search ───────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return !q
      ? items
      : items.filter(
          (item) =>
            item.name.toLowerCase().includes(q) ||
            item.description.toLowerCase().includes(q) ||
            item.category.toLowerCase().includes(q),
        );
  }, [items, search]);

  const installedPlugins = pluginRegistry.list();

  // Determine what to show in the main list
  const showFallback = !loading && !!error;
  const showApiList = !loading && !error && apiPlugins.length > 0;
  const showEmpty = !loading && !error && apiPlugins.length === 0;

  // ── Consent modal handlers ───────────────────────────────────────────────
  const handleConsentAccept = useCallback(() => {
    const pending = pendingConsent;
    setPendingConsent(null);
    if (!pending) return;
    if (pending.kind === 'remote') {
      void performApiInstall(pending.plugin);
    } else {
      performBundledInstall(pending.manifest);
    }
  }, [pendingConsent, performApiInstall]);
  const handleConsentCancel = useCallback(() => {
    setPendingConsent(null);
  }, []);

  return (
    <div className="marketplace-panel">
      {reportingPlugin && (
        <PluginReportModal
          pluginId={reportingPlugin.id}
          pluginName={reportingPlugin.name}
          onClose={() => setReportingPlugin(null)}
        />
      )}
      {pendingConsent && (
        <PluginConsentModal
          pluginName={
            pendingConsent.kind === 'remote'
              ? pendingConsent.plugin.name
              : pendingConsent.manifest.name
          }
          pluginAuthor={
            pendingConsent.kind === 'remote'
              ? pendingConsent.plugin.author
              : pendingConsent.manifest.author
          }
          pluginVersion={
            pendingConsent.kind === 'remote'
              ? pendingConsent.plugin.version
              : pendingConsent.manifest.version
          }
          permissions={
            pendingConsent.kind === 'remote'
              ? pendingConsent.plugin.permissions
              : pendingConsent.manifest.permissions
          }
          onAccept={handleConsentAccept}
          onCancel={handleConsentCancel}
        />
      )}
      <div className="panel-header">
        <span className="panel-title">{t('marketplace.title')}</span>
        <button
          type="button"
          className="btn-report"
          onClick={() => setShowPublisher((v) => !v)}
          title={t('marketplace.submitPlugin', { defaultValue: 'Submit a plugin' })}
        >
          {showPublisher ? 'Back to catalogue' : 'Publish'}
        </button>
      </div>
      {showPublisher && <PluginPublisherPanel userEmail={userEmail} />}

      <input
        type="text"
        placeholder={t('marketplace.searchPlaceholder', { defaultValue: 'Search plugins…' })}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="marketplace-search"
        aria-label={t('marketplace.searchAria', { defaultValue: 'Search plugins' })}
      />

      {/* Loading skeleton */}
      {loading && (
        <div className="marketplace-section" aria-label={t('marketplace.loading', { defaultValue: 'Loading plugins' })}>
          <PluginSkeleton />
          <PluginSkeleton />
          <PluginSkeleton />
        </div>
      )}

      {/* Fallback catalogue — shown when the remote marketplace is unreachable
          (or, currently, not yet deployed). The bundled plugins are a real
          usable set, so present them as the default list with a subtle hint
          rather than a red "error" banner that makes the feature look broken.
          No Refresh button: there's no remote catalogue to fetch yet, so it
          would lie about doing something. */}
      {showFallback && (
        <div className="marketplace-section">
          <div className="marketplace-notice">
            <span className="marketplace-notice-text">
              {t('marketplace.bundledCaption')}
            </span>
          </div>
          {AVAILABLE_PLUGINS.map((plugin) => {
            const isInstalled = registeredIds.has(plugin.id);
            return (
              <div key={plugin.id} className="marketplace-item">
                <div className="item-info">
                  <span className="item-name">{plugin.name}</span>
                  <span className="item-desc">{plugin.description}</span>
                  <span className="item-meta">v{plugin.version}</span>
                </div>
                <div className="item-actions">
                  {isInstalled ? (
                    <button
                      aria-label={`Uninstall ${plugin.name}`}
                      className="btn-uninstall"
                      onClick={() => handleUninstallPlugin(plugin.id)}
                    >
                      Uninstall
                    </button>
                  ) : (
                    <button
                      aria-label={`Install ${plugin.name}`}
                      className="btn-install"
                      onClick={() => handleInstallPlugin(plugin)}
                    >
                      Install
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* API-backed list */}
      {showApiList && (
        <div className="marketplace-section">
          <h4 className="section-title">Available</h4>
          {apiPlugins.map((plugin) => {
            const isInstalling = installingIds.has(plugin.id);
            const isUninstalling = uninstallingIds.has(plugin.id);
            return (
              <div key={plugin.id} className={`marketplace-item${plugin.installed ? ' installed' : ''}`}>
                <div className="item-info">
                  {plugin.icon && <img src={plugin.icon} alt="" className="plugin-icon" />}
                  <span className="item-name">{plugin.name}</span>
                  <span className="item-desc">{plugin.description}</span>
                  <span className="item-meta">
                    v{plugin.version} · {plugin.price === 'free' ? 'Free' : `$${plugin.price}`}
                    {plugin.rating > 0 && ` · ★ ${plugin.rating.toFixed(1)}`}
                  </span>
                </div>
                <div className="item-actions">
                  {plugin.installed && hasUpdate(plugin) && (
                    <button
                      aria-label={`Update ${plugin.name}`}
                      className="btn-update"
                      disabled={isInstalling}
                      onClick={() => void handleApiUpdate(plugin)}
                      title={`Update to v${plugin.version}`}
                    >
                      {isInstalling ? t('marketplace.update') + '…' : t('marketplace.update')}
                    </button>
                  )}
                  {plugin.installed ? (
                    <button
                      aria-label={`${t('marketplace.uninstall')} ${plugin.name}`}
                      className="btn-uninstall"
                      disabled={isUninstalling}
                      onClick={() => void handleApiUninstall(plugin.id)}
                    >
                      {isUninstalling ? t('marketplace.uninstall') + '…' : t('marketplace.uninstall')}
                    </button>
                  ) : (
                    <button
                      aria-label={`${t('marketplace.install')} ${plugin.name}`}
                      className="btn-install"
                      disabled={isInstalling}
                      onClick={() => void handleApiInstall(plugin)}
                    >
                      {isInstalling ? t('marketplace.install') + '…' : t('marketplace.install')}
                    </button>
                  )}
                  <button
                    aria-label={`${t('marketplace.report')} ${plugin.name}`}
                    className="btn-report"
                    onClick={() => setReportingPlugin({ id: plugin.id, name: plugin.name })}
                    title={t('marketplace.report')}
                  >
                    {t('marketplace.report')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showEmpty && (
        <div className="marketplace-empty">{t('marketplace.empty', { defaultValue: 'No plugins found.' })}</div>
      )}

      {/* Legacy prop-based items (used in tests / embedded mode) */}
      {filtered.length > 0 && (
        <div className="marketplace-section">
          {filtered.map((item) => (
            <div key={item.id} className="marketplace-item">
              <div className="item-info">
                <span className="item-name">{item.name}</span>
                <span className="item-desc">{item.description}</span>
                <span className="item-meta">v{item.version} · {item.category} · {item.downloads.toLocaleString()} downloads</span>
              </div>
              <div className="item-actions">
                {item.installed ? (
                  <span className="installed-badge">Installed</span>
                ) : (
                  <button aria-label={`Install ${item.name}`} className="btn-install" onClick={() => onInstall?.(item)}>
                    Install
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My installed plugins */}
      <div className="marketplace-section">
        <h4 className="section-title">{t('marketplace.myPlugins')}</h4>
        {installedPlugins.length === 0 ? (
          <div className="marketplace-empty">{t('marketplace.noInstalled')}</div>
        ) : (
          installedPlugins.map((plugin) => (
            <div key={plugin.id} className="marketplace-item installed">
              <div className="item-info">
                <span className="item-name">{plugin.name}</span>
                <span className="item-meta">v{plugin.version}</span>
              </div>
              <div className="item-actions">
                <button
                  aria-label={`Uninstall ${plugin.name}`}
                  className="btn-uninstall"
                  onClick={() => handleUninstallPlugin(plugin.id)}
                >
                  Uninstall
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
